import {createExtensionLoader} from '@baidu/ku-tool-sdk/dist/extension-loader';

import {doc} from './sdk/doc';
import {repository} from './sdk/repository';
import {component} from './sdk/component';
import {user} from './sdk/user';
import {utils} from './sdk/utils';
import {initDropMenuTool, initSideBarTool} from './sdk/init';
import {appLog} from './sdk/log';
import kuToolEvent, {getKuToolEvent} from './eventBus/bus';
import './sdk/event';

import type {IKuToolSDKInstance} from './types';
import type {InitDropMenuParams, InitSideBarParams} from './types/slot';
import {reportCallAPI, reportCallToolAPI} from './buried';
import {createEditorExtension} from './sdk/editor-extension';
import {getEditorSdkGenerator} from './sdk/editor';
import {slot} from './sdk/slot';
import * as store from '@/store';
import {apiParamsHandleForReport} from './utils';

export * from './utils';

const filterFnList = ['initDropMenuTool', 'initSideBarTool'];

/**
 * 在调用函数之前执行的操作
 *
 * @param fnName 要调用的函数名
 * @param fnParams 函数的参数
 * @param toolId 工具ID
 * @param debug 是否为调试模式
 */
function preInvoke(fnName: string, fnParams: any, toolId: string, debug?: boolean) {
    if (debug) {
        return;
    }

    const apiParams = apiParamsHandleForReport(fnParams);

    // 埋点上报
    reportCallToolAPI({
        id: toolId,
        apiName: fnName,
        apiParams,
    });
}

/**
 * 【版本 2】在调用函数之前执行的操作
 * @param fnName 要调用的函数名
 * @param fnParams 函数的参数
 * @param toolId 工具ID
 * @param debug 是否为调试模式
 * @param apiPath 接口调用路径
 */
function preInvokeV2(fnName: string, fnParams: any, toolId: string, debug?: boolean, apiPath = '') {
    const apiParams = apiParamsHandleForReport(fnParams);

    // 埋点上报
    reportCallAPI({
        id: toolId,
        apiName: fnName,
        apiParams,
        apiPath,
    }, debug);
}

/**
 * 创建一个代理对象
 *
 * @param obj 目标对象
 * @param root 根对象，默认为目标对象
 * @returns 返回一个符合 IKuToolSDKInstance 接口规范的代理对象
 */
function createProxy(obj: any, root?: any, debug?: boolean, path: string[] = []): IKuToolSDKInstance {
    root = root || obj;

    const handler = {
        get(target: any, prop: string, receiver: any) {
            const value = Reflect.get(target, prop, receiver);
            // 构建当前访问的完整路径
            const currentPath = [...path, prop];

            if (typeof value === 'function') {
                const pathStr = currentPath.join('.');
                // 对于 filterFnList 的函数，目前只有埋点的逻辑不同
                if (filterFnList.includes(prop)) {
                    return (...args: any) => {
                        preInvokeV2(prop, args, root.id, debug, pathStr);
                        return value.apply(target, args);
                    };
                } else {
                    // 如果是方法，返回一个包装后的函数
                    return (...args: any) => {
                        preInvoke(prop, args, root.id, debug);
                        preInvokeV2(prop, args, root.id, debug, pathStr);
                        return value.apply(target, args);
                    };
                }
            } else if (typeof value === 'object' && value !== null) {
                // 如果是对象，递归创建代理
                return createProxy(value, root, debug, currentPath);
            }
            return value;
        },
    };

    // 创建并返回代理对象
    return new Proxy(obj, handler);
}

const editorExtension = createEditorExtension(store);
const editorSdkGenerator = getEditorSdkGenerator(store);

const getExtensionLoader = () => {
    const extensionLoader = createExtensionLoader();
    return extensionLoader;
};

const createSDK = (options?: {appId: string, debug?: boolean}) => {
    const debug = options?.debug;
    const instance: IKuToolSDKInstance = {
        id: options?.appId || '',
        initSideBarTool: (opts: InitSideBarParams) => initSideBarTool(instance, opts),
        initDropMenuTool: (opts: InitDropMenuParams) => initDropMenuTool(instance, opts),
        slot,
        doc,
        repository,
        component,
        user,
        utils,
        appLog,
        event: kuToolEvent,
        editorExtension,
        editor: editorSdkGenerator(),
        debug,
    };

    const proxyInstance = createProxy(instance, undefined, debug);

    if (options?.appId) {
        getExtensionLoader().setSdk(options.appId, proxyInstance as any);
    }

    return proxyInstance;
};

const ToolSDK = {
    createSDK,
    getExtensionLoader,
    getKuToolEvent,
};

// eslint-disable-next-line no-underscore-dangle
(window as any).__KU_TOOL_SDK__ = ToolSDK;

export {
    createSDK,
};
